import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Shell = ({ children, testId }) => (
  <section data-testid={testId} className="mx-auto flex max-w-7xl justify-center px-4 py-24 sm:px-6 lg:px-12">
    <div className="card-tech w-full max-w-lg rounded-2xl p-8 text-center sm:p-10">{children}</div>
  </section>
);

const Back = () => {
  const { user } = useAuth();
  const to = user?.role === "client" ? "/portal/payments" : user?.role === "admin" ? "/admin/payments" : "/services";
  return <Link to={to} data-testid="payment-result-back-link" className="mt-8 inline-block rounded-full border border-white/15 px-5 py-2 text-sm text-slate-200 hover:border-cyan-400/50">{user ? "Back to payments" : "Back to services"}</Link>;
};

export function PaymentCancel() {
  return (
    <Shell testId="payment-cancel-page">
      <XCircle className="mx-auto h-10 w-10 text-amber-300" />
      <h1 data-testid="payment-cancel-message" className="mt-4 text-2xl font-bold text-white">Payment cancelled — you have not been charged.</h1>
      <Back />
    </Shell>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return undefined; }
    let timer;
    const poll = async () => {
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setState("paid");
        if (data.payment_status === "expired") return setState("error");
      } catch (e) {
        if (e.response?.status === 404) return setState("error");
      }
      if (tries.current >= 15) return setState("slow");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <Shell testId="payment-success-page">
      {state === "paid" ? (
        <>
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-300" />
          <h1 data-testid="payment-success-message" className="mt-4 text-2xl font-bold text-white">Payment received — thank you!</h1>
          <p className="mt-2 text-sm text-slate-400">A receipt is on its way to your inbox.</p>
        </>
      ) : state === "checking" ? (
        <>
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-cyan-300" />
          <h1 data-testid="payment-checking-message" className="mt-4 text-xl font-semibold text-white">Confirming your payment…</h1>
        </>
      ) : (
        <>
          <XCircle className="mx-auto h-10 w-10 text-amber-300" />
          <h1 data-testid="payment-pending-message" className="mt-4 text-xl font-semibold text-white">
            {state === "slow" ? "We're still confirming your payment. Refresh this page in a minute." : "We couldn't confirm this payment."}
          </h1>
        </>
      )}
      <Back />
    </Shell>
  );
}
